// ✅ ManageHelp.js – Admin Help Center Page
import React, { useEffect, useState } from "react";
import axios from "axios";
import Sidebar from "../components/Sidebar";
import { toast } from "react-hot-toast";

const API_BASE = process.env.REACT_APP_API_BASE; // ✅ unified API root

const ManageHelp = () => {
  const [tickets, setTickets] = useState([]);
  const [filter, setFilter] = useState("all");
  const [userTypeFilter, setUserTypeFilter] = useState("all");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [note, setNote] = useState("");
  const [status, setStatus] = useState("resolved");
  const [loading, setLoading] = useState(false);

  // ============================================================
  // 📡 Fetch All Help Tickets
  // ============================================================
  const loadTickets = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/admin/help/all`);
      setTickets(res.data || []);
    } catch (err) {
      console.error("❌ Failed to fetch tickets:", err);
      toast.error("Failed to fetch help tickets.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTickets();
  }, []);

  // ============================================================
  // 🔁 Filtered Tickets
  // ============================================================
  const filteredTickets = tickets
    .filter((t) => (filter === "all" ? true : t.status === filter))
    .filter((t) => (userTypeFilter === "all" ? true : t.userType === userTypeFilter));

  // ============================================================
  // 🧾 Update Ticket
  // ============================================================
  const updateTicket = async (id) => {
    try {
      await axios.post(`${API_BASE}/admin/help/update/${id}`, {
        status,
        adminNotes: note,
      });
      toast.success("✅ Ticket updated successfully!");
      await loadTickets();
      setSelectedTicket(null);
      setNote("");
      setStatus("resolved");
    } catch (err) {
      console.error("❌ Failed to update ticket:", err);
      toast.error("Failed to update ticket.");
    }
  };

  return (
    <div className="flex font-[Poppins]">
      <Sidebar />
      <main className="ml-0 md:ml-64 p-6 w-full bg-gray-100 min-h-screen">
        <h1 className="text-3xl font-extrabold mb-8 text-gray-800">🛠️ Help Center</h1>

        {/* Filters */}
        <div className="flex flex-wrap gap-6 mb-6">
          <div>
            <label className="mr-2 font-semibold">Status:</label>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="border px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All</option>
              <option value="open">Open</option>
              <option value="in-progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
          </div>

          <div>
            <label className="mr-2 font-semibold">User Type:</label>
            <select
              value={userTypeFilter}
              onChange={(e) => setUserTypeFilter(e.target.value)}
              className="border px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All</option>
              <option value="customer">Customer</option>
              <option value="worker">Worker</option>
            </select>
          </div>
        </div>

        {/* Tickets Grid */}
        {loading ? (
          <p className="text-gray-500">Loading help tickets...</p>
        ) : filteredTickets.length === 0 ? (
          <p className="text-gray-500">No tickets found.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filteredTickets.map((ticket) => (
              <div
                key={ticket._id}
                className="bg-white p-5 rounded-xl shadow hover:shadow-lg transition border"
              >
                <h2 className="text-lg font-bold mb-1">{ticket.subject}</h2>
                <p className="text-sm text-gray-700">
                  <strong>Email:</strong> {ticket.email}
                </p>
                <p className="text-sm text-gray-700">
                  <strong>Type:</strong> {ticket.userType}
                </p>
                <p className="text-sm">
                  <strong>Status:</strong>{" "}
                  <span className="capitalize font-semibold text-indigo-700">
                    {ticket.status}
                  </span>
                </p>
                <p className="text-gray-700 mt-2 text-sm line-clamp-3">
                  {ticket.message}
                </p>
                <button
                  onClick={() => setSelectedTicket(ticket)}
                  className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 transition"
                >
                  View & Respond
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Modal */}
        {selectedTicket && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-xl w-full max-w-lg shadow-2xl relative">
              <button
                onClick={() => setSelectedTicket(null)}
                className="absolute top-3 right-4 text-xl text-gray-500 hover:text-red-500"
              >
                ✕
              </button>

              <h2 className="text-2xl font-bold mb-2 text-gray-800">
                {selectedTicket.subject}
              </h2>
              <p>
                <strong>From:</strong> {selectedTicket.email}
              </p>
              <p>
                <strong>User Type:</strong> {selectedTicket.userType}
              </p>
              <p className="mt-2 text-sm text-gray-800">{selectedTicket.message}</p>

              <textarea
                className="w-full border rounded-lg p-3 mt-4 text-sm focus:ring-2 focus:ring-indigo-500"
                rows="4"
                placeholder="Write a note or response..."
                value={note}
                onChange={(e) => setNote(e.target.value)}
              ></textarea>

              <div className="flex justify-between items-center mt-4">
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="border px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="open">Open</option>
                  <option value="in-progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>

                <div className="flex gap-2">
                  <button
                    onClick={() => setSelectedTicket(null)}
                    className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-100"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => updateTicket(selectedTicket._id)}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm"
                  >
                    Update Ticket
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ManageHelp;
